import React, { useEffect, useState } from 'react';
import Axios, { AxiosProgressEvent } from 'axios';
import { XMLParser } from "fast-xml-parser";

import { EUploadStatus } from '../../@types/Upload';

import { AxiosClient } from '../../utils/axios';
import { FormatBytes } from '../../utils/formatter';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { SetLoading } from '../../store/layout.slice';
import { SetUploadStatus } from '../../store/translate.page.slice';
import { AnalyzeXMLObject, IsValidSrtContent } from '../../utils/validator';
import { EConvertType } from '../../@types/Convert';

const xmlParser = new XMLParser();
interface Props {
    file: File;
    onFileUploadCompleted: (url: string) => void;
    onFileRemove: () => void;
}

function UploadingCard(props: Props) {
    const dispatch = useAppDispatch();

    const convertType: EConvertType | null = useAppSelector((state) => state.pageConvert.convertConfig.type);

    const [status, setStatus] = useState<EUploadStatus>(EUploadStatus.NoFileSelected);
    const [statusText, setStatusText] = useState<string>('');
    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if(status !== EUploadStatus.NoFileSelected)
            return;

        new Promise(async (resolve, reject) => {
            switch(convertType) {
                case EConvertType.SrtToFCPXML:
                case EConvertType.SrtToAvidSubCap:
                case EConvertType.SrtToEDL:
                    setStatus(EUploadStatus.Verifying);
                    setStatusText('Verifying subtitle file...');

                    try {
                        await new Promise((res, rej) => {
                            props.file.text().then((value: string) => {
                                if(!IsValidSrtContent(value)) rej(`Invalid subtitle file`);
                                else res(true);
                            }).catch((err) => {
                                rej(`Unable to read srt file`);
                            })
                        })
                    } catch(err) {
                        return reject(err);
                    }
                    break;
                case EConvertType.XMLToFCPXML:
                    setStatus(EUploadStatus.Verifying);
                    setStatusText('Verifying XML file...');

                    try {
                        await new Promise((res, rej) => {
                            props.file.text().then((value: string) => {
                                let obj = AnalyzeXMLObject(xmlParser.parse(value));
                                if(!obj) rej(`Invalid XML file`);
                                else res(true);
                            }).catch((err) => {
                                rej(`Unable to read XML file`);
                            })
                        })
                    } catch(err) {
                        return reject(err);
                    }
                    break;
                default: return reject('Invalid convert type');
            }

            setStatusText('Preparing to upload...');
            var signedUrl: string = '';
            try {
                let { data } = await AxiosClient.post(`/prepare-upload`, { fileName: props.file.name, contentType: props.file.type || 'application/octet-stream' });
                signedUrl = data.presignedUrl;

                dispatch(SetLoading(false));
            } catch(e) {
                return reject(`Unable to request for upload permission`);
            }

            try {
                dispatch(SetUploadStatus(EUploadStatus.Uploading));
                setStatus(EUploadStatus.Uploading);
                setStatusText('Uploading...');

                await Axios.put(signedUrl, props.file, {
                    headers: {
                        'Content-Type': props.file.type || 'application/octet-stream'
                    },
                    onUploadProgress: (progress: AxiosProgressEvent) => {
                        setProgress(Math.round((progress.progress || 0) * 100));
                    }
                });
            } catch(e: any) {
                return reject(e?.message || 'Something went wrong');
            }

            setProgress(100);

            setStatus(EUploadStatus.Uploaded);
            setStatusText('Upload completed');
            props.onFileUploadCompleted(signedUrl.split('?')[0]);

            resolve(true);
        }).then(() => {

        }).catch((message) => {
            setStatus(EUploadStatus.Failed);
            setStatusText(`Upload failed (${message})`);
        });
    }, [props.file, convertType]);

    const onClickFileRemove = () => {
        setStatus(EUploadStatus.NoFileSelected);
        setStatusText('');
        setProgress(0);

        props.onFileRemove();
    }

    return (
        <div className="w-full rounded overflow-hidden shadow-lg px-6 py-6 relative">
            { [EUploadStatus.Verifying, EUploadStatus.Uploading].indexOf(status) !== -1 && <div className='absolute top-5 right-5'><div className="simple-loader"></div></div> }
            <div className="mb-3">
                <div className='flex justify-between items-center font-bold text-xl mb-2'>
                    <p className='mb-0'><i className="fa-solid fa-cloud-arrow-up mr-1.5"></i>{ statusText }</p>
                    { [EUploadStatus.Uploaded, EUploadStatus.Failed].indexOf(status) !== -1 && <p onClick={onClickFileRemove} className='mb-0 transition text-gray-600 hover:cursor-pointer hover:text-gray-700 active:text-gray-900'><i className="fa-solid fa-circle-xmark"></i></p> }
                </div>
                <p className="text-gray-700 text-base">{ props.file.name } <span className=''>({FormatBytes(props.file.size)})</span></p>
            </div>
            <div className="w-full bg-gray-200 rounded-full dark:bg-gray-700">
                <div className={`text-xs font-medium text-center p-0.5 leading-none rounded-full ${status !== EUploadStatus.Uploaded ? 'bg-blue-600 text-blue-100' : 'bg-green-600 text-green-100'}`} style={{ width: `${progress}%` }}>{ progress }%</div>
            </div>
        </div>
    )
}

export default React.memo(UploadingCard);